import React, { useState } from 'react';
import cx from 'classnames';
import { useForm } from 'react-hook-form';
import { batch } from 'react-redux';

import DentalButton from 'components/Buttons/DentalButton/DentalButton';
import FormTextInput from 'components/BookingForm/FormTextInput/FormTextInput';
import { formatDobForBE } from 'components/BookingForm/utils/helperFunction';
import useIsLoadedInsideIframe from 'hooks/useIsLoadedInsideIframe';
import { Insurance } from 'interfaces/insuranceType';
import {
  STEPS,
  popFamilyStep,
  pushFamilyStep,
  setContactUser,
  updateFBStep2,
} from 'redux/familyBookingSlice';
import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';
import {
  emailValidation,
  firstNameValidation,
  lastNameValidation,
} from 'utils/inputValidation';

import ContactInsurance from './ContactInsurance/ContactInsurance';
import styles from './ContactDetails.module.scss';

const { SCHEDULE } = STEPS;

const ContactDetails = () => {
  const {
    familyBookingSlice: {
      step1: { phoneNumber, dob },
      step2: { contactPatient },
    },
    authSlice: { isLoggedIn },
    clinicDetailsSlice: { clinicData },
  } = useAppSelector((state) => state);
  const dispatch = useAppDispatch();
  const isLoadedInsideIframe = useIsLoadedInsideIframe();

  const isInsuranceEnabled = clinicData.isInsuranceEnabled;

  const [insurance, setInsurance] = useState<Insurance | null>(
    contactPatient.insurance
  );
  const [isPayForMyself, setIsPayForMyself] = useState(
    contactPatient.isPayForMyself
  );
  const [insurancePaymentMethod, setInsurancePaymentMethod] = useState(
    contactPatient.insurancePaymentMethod
  );
  const { control, getValues, formState } = useForm({
    mode: 'all',
    defaultValues: {
      firstName: contactPatient.firstName,
      lastName: contactPatient.lastName,
      email: contactPatient.email,
    },
  });

  const isInsuranceSelectionMissing = !insurance && !isPayForMyself;

  const isPaymentMethodMissing = isPayForMyself && !insurancePaymentMethod;

  const isInsuranceInactive =
    !isPayForMyself && !!insurance && !insurance.isActive;

  const isInsuranceIncomplete =
    isInsuranceEnabled &&
    (isInsuranceSelectionMissing ||
      isPaymentMethodMissing ||
      isInsuranceInactive);

  const isNextDisabled = !formState.isValid || isInsuranceIncomplete;

  const handleOnBackClick = () => {
    dispatch(popFamilyStep());
  };

  const handleOnNextClick = () => {
    const email = getValues('email');
    const firstName = getValues('firstName');
    const lastName = getValues('lastName');

    batch(() => {
      dispatch(
        updateFBStep2({
          contactPatient: {
            email,
            firstName,
            lastName,
            insurance,
            isPayForMyself,
            insurancePaymentMethod,
          },
        })
      );

      dispatch(
        setContactUser({
          email,
          firstName,
          lastName,
          dob: formatDobForBE(dob),
          phoneNumber,
        })
      );

      dispatch(pushFamilyStep(SCHEDULE));
    });
  };

  return (
    <form noValidate className={styles['contact-form']}>
      <div className={styles['otp-form']}>
        <div className={styles['card-title']}>Just A Few Details</div>
        <div className={styles['card-content']}>
          <div>
            <FormTextInput
              type="text"
              name="email"
              label="Email address"
              control={control}
              placeholder="Input your email address"
              required="This field is required"
              rules={{
                validate: (value: string) => {
                  if (isLoggedIn) return true;

                  if (!emailValidation(value)) {
                    return 'Invalid email address';
                  }
                },
              }}
              disabled={isLoggedIn}
            />
            <div className={styles['name-section']}>
              <FormTextInput
                type="text"
                name="firstName"
                label="First Name"
                control={control}
                placeholder="Input your first name"
                required="This field is required"
                rules={{
                  validate: (value: string) => {
                    if (isLoggedIn) return true;

                    const message = firstNameValidation(value);

                    return message || true;
                  },
                }}
                disabled={isLoggedIn}
              />
              <FormTextInput
                type="text"
                name="lastName"
                label="Last Name"
                control={control}
                placeholder="Input your last name"
                required="This field is required"
                rules={{
                  validate: (value: string) => {
                    if (isLoggedIn) return true;

                    const message = lastNameValidation(value);

                    return message || true;
                  },
                }}
                disabled={isLoggedIn}
              />
            </div>
            {isInsuranceEnabled && (
              <ContactInsurance
                insurance={insurance}
                isPayForMyself={isPayForMyself}
                insurancePaymentMethod={insurancePaymentMethod}
                onChangeInsurance={setInsurance}
                onTogglePayForMyself={() => {
                  setIsPayForMyself((value) => !value);
                  setInsurance(null);
                  setInsurancePaymentMethod(null);
                }}
                onChangeInsurancePaymentMethod={setInsurancePaymentMethod}
              />
            )}
          </div>
        </div>
      </div>
      <div
        className={cx(styles['button-container'], {
          [styles['button-container-iframe']]: isLoadedInsideIframe,
        })}
      >
        {!isLoggedIn && (
          <DentalButton
            className={styles['button']}
            variant="outlined"
            onClick={handleOnBackClick}
          >
            Back
          </DentalButton>
        )}
        <DentalButton
          className={styles['button']}
          variant="contained"
          onClick={handleOnNextClick}
          disabled={isNextDisabled}
        >
          Next
        </DentalButton>
      </div>
    </form>
  );
};

export default ContactDetails;
